import React, { useState, useEffect, useRef } from "react";
import "./Page1Product.css";
import { usePost } from "../../../../hooks/usePost";

const toEnglishDigits = (str) => {
  const persianNumbers = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
  const arabicNumbers = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];
  return str.replace(/[\u0660-\u0669\u06f0-\u06f9]/g, (c) => {
    let d = persianNumbers.indexOf(c);
    if (d === -1) d = arabicNumbers.indexOf(c);
    return d;
  });
};

const formatNumberWithCommas = (value) => {
  if (!value) return "";
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

// کامپوننت InputField (اسکرول دستی حذف شد، فقط پراپ‌ها پاس داده می‌شوند)
const InputField = ({ label, required, error, hint, ...props }) => {
  return (
    <div className="p1-field">
      <label className="p1-label">
        {label} {required && <span className="p1-required">*</span>}
      </label>
      <input
        {...props}
        className={`p1-input ${error ? 'p1-input--error' : ''}`}
      />
      {error && <span className="p1-error">{error}</span>}
      {hint && !error && <p className="p1-hint">{hint}</p>}
    </div>
  );
};

// کامپوننت TextareaField (فقط تغییر ارتفاع خودکار ماند، اسکرول دستی حذف شد)
const TextareaField = ({ label, required, error, hint, ...props }) => {
  const textareaRef = useRef(null);

  const handleFocus = (e) => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
    if (props.onFocus) props.onFocus(e);
  };

  const handleBlur = (e) => {
    if (textareaRef.current) {
      textareaRef.current.style.height = ''; // بازگشت به ارتفاع پیش‌فرض (جمع شدن)
    }
    if (props.onBlur) props.onBlur(e);
  };

  const handleChange = (e) => {
    // اگر در حال تایپ است و فیلد فوکوس دارد، ارتفاع را لحظه‌ای تنظیم کن
    if (textareaRef.current && document.activeElement === textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
    if (props.onChange) props.onChange(e);
  };

  return (
    <div className="p1-field">
      <label className="p1-label">
        {label} {required && <span className="p1-required">*</span>}
      </label>
      <textarea
        {...props}
        ref={textareaRef}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        className={`p1-textarea ${error ? 'p1-input--error' : ''}`}
        style={{ overflowY: 'hidden' }} /* جلوگیری از نمایش اسکرول‌بار مزاحم هنگام تغییر ارتفاع */
      />
      {error && <span className="p1-error">{error}</span>}
      {hint && !error && <p className="p1-hint">{hint}</p>}
    </div>
  );
};

// کش سراسری
let globalCategoryCache = {
  business_id: null,
  data: [],
  page: 1,
  lastFetchedPage: 0,
  endPage: false,
  timestamp: 0
};

export const clearCategoryCache = () => {
  globalCategoryCache = {
    business_id: null,
    data: [],
    page: 1,
    lastFetchedPage: 0,
    endPage: false,
    timestamp: 0
  };
};

export default function Page1Product({ onNext, onPrev, isFirst, isLast, initialData, business_id }) {

  const CACHE_LIFETIME = 15000;
  const now = Date.now();

  if (globalCategoryCache.timestamp > 0 && (now - globalCategoryCache.timestamp > CACHE_LIFETIME)) {
    clearCategoryCache();
    globalCategoryCache.business_id = business_id;
    globalCategoryCache.timestamp = now;
  } else if (globalCategoryCache.business_id !== null && globalCategoryCache.business_id !== business_id) {
    clearCategoryCache();
    globalCategoryCache.business_id = business_id;
    globalCategoryCache.timestamp = now;
  } else if (globalCategoryCache.business_id === null) {
    globalCategoryCache.business_id = business_id;
    globalCategoryCache.timestamp = now;
  }

  const [commodityGroup, setCommodityGroup] = useState(globalCategoryCache.data);
  const [pageCommodityGroup, setPageCommodityGroup] = useState(globalCategoryCache.page);
  const [endPageCommodityGroup, setEndPageCommodityGroup] = useState(globalCategoryCache.endPage);

  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const sentinelRef_CommodityGroup = useRef();

  const shouldFetch = (pageCommodityGroup > globalCategoryCache.lastFetchedPage && !endPageCommodityGroup) || (commodityGroup.length === 0 && !endPageCommodityGroup);

  const apiPath = shouldFetch ? `commodity/group/show/all?page=${pageCommodityGroup}` : null;
  const { data: DataGroup } = usePost(apiPath, { id_business: business_id, row_count: 8 });

  useEffect(() => {
    if (shouldFetch) {
      setIsLoadingGroups(true);
    }
  }, [shouldFetch]);

  useEffect(() => {
    if (DataGroup && DataGroup.data) {
      setIsLoadingGroups(false);

      globalCategoryCache.lastFetchedPage = pageCommodityGroup;
      globalCategoryCache.timestamp = Date.now();

      setCommodityGroup((prev) => {
        const newItems = DataGroup.data.data.filter(
          (newItem) => !prev.some((existingItem) => existingItem.id === newItem.id)
        );
        const updatedList = [...prev, ...newItems];

        globalCategoryCache.data = updatedList;
        return updatedList;
      });

      const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          if (pageCommodityGroup < DataGroup.data.last_page) {
            setPageCommodityGroup((prevPage) => {
              const nextPage = prevPage + 1;
              globalCategoryCache.page = nextPage;
              return nextPage;
            });
          } else {
            setEndPageCommodityGroup(true);
            globalCategoryCache.endPage = true;
          }
        }
      }, {
        root: null,
        rootMargin: "0px",
        threshold: 1.0,
      });

      const sentinelNode = sentinelRef_CommodityGroup.current;
      if (sentinelNode) {
        observer.observe(sentinelNode);
      }

      return () => {
        if (sentinelNode) {
          observer.unobserve(sentinelNode);
        }
      };
    }
  }, [DataGroup, pageCommodityGroup]);

  const [form, setForm] = useState({
    name: "",
    category: "",
    shortDesc: "",
    fullDesc: "",
    instagram: "",
    sku: "",
    discount: "",
    vatPercent: "",
    packagingCost: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (initialData) {
      setForm(initialData);
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "packagingCost" || name === "vatPercent" || name === "discount" || name === "sku") {
      let numValue = toEnglishDigits(value).replace(/[^0-9]/g, "");

      if (name === "vatPercent" || name === "discount") {
        numValue = numValue.slice(0, 2);
        setForm({ ...form, [name]: numValue });
      }
      else if (name === "packagingCost") {
        numValue = numValue.slice(0, 10);
        setForm({ ...form, [name]: formatNumberWithCommas(numValue) });
      }
      else if (name === "sku") {
        setForm({ ...form, [name]: numValue });
      }
      return;
    }

    setForm({ ...form, [name]: value });
  };

  const isValidInstagram = (url) => {
    if (!url || !url.trim()) return true;
    return /^(https?:\/\/)?(www\.)?instagram\.com\/[a-zA-Z0-9._\-\/]+(\/)?(\?[^\s]*)?$/i.test(url.trim());
  };


  const validateForm = () => {
    const newErrors = {};

    if (!form.name.trim()) {
      newErrors.name = "نام محصول الزامی است";
    }

    if (form.instagram && !isValidInstagram(form.instagram)) {
      newErrors.instagram = "لینک اینستاگرام معتبر نیست";
    }

    if (form.discount && !/^(\d+%?|\d+ درصد)$/.test(form.discount)) {
      newErrors.discount = "فرمت تخفیف معتبر نیست (مثال: 20%)";
    }
    if (form.vatPercent && !/^(\d+%?|\d+ درصد)$/.test(form.vatPercent)) {
      newErrors.vatPercent = "فرمت ارزش افزوده معتبر نیست (مثال: 10%)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    setTimeout(() => {
      setIsSubmitting(false);
      const formDataToSend = {
        ...form,
        packagingCost: form.packagingCost ? form.packagingCost.toString().replace(/,/g, "") : ""
      };
      onNext(formDataToSend);
    }, 100);
  };

  return (
    <form className="p1-card" onSubmit={handleSubmit}>
      {/* 1. نام محصول */}
      <InputField
        label="نام محصول"
        required
        name="name"
        value={form.name}
        onChange={handleChange}
        placeholder="نام محصول را وارد کنید"
        error={errors.name}
      />

      {/* 2. توضیحات کوتاه */}
      <InputField
        label="توضیحات کوتاه"
        name="shortDesc"
        value={form.shortDesc}
        onChange={handleChange}
        placeholder="این توضیحات در نمایش اولیه محصول رویت می‌شود"
      />

      {/* 3. توضیحات کلی */}
      <TextareaField
        label="توضیحات کلی"
        name="fullDesc"
        value={form.fullDesc}
        onChange={handleChange}
        placeholder="توضیحات کامل محصول را وارد کنید"
        rows={1}
      />

      {/* ساختار دو ستونه */}
      <div className="p1-grid-2col">

        {/* ردیف اول شبکه */}
        <div className="p1-field">
          <label className="p1-label">دسته کالا</label>
          <div className="p1-select-wrap">
            <select
              name="category"
              value={form.category}
              onChange={handleChange}
              // متد onFocus مربوط به اسکرول از اینجا حذف شد
              className="p1-select"
              disabled={isLoadingGroups && commodityGroup.length === 0}
            >
              <option value="">
                {isLoadingGroups ? "در حال دریافت..." : "انتخاب کنید"}
              </option>
              {commodityGroup.map(c => (
                <option key={c.id} value={c.id}>{c.title}</option>
              ))}
            </select>
            <div ref={sentinelRef_CommodityGroup} style={{ height: '1px' }}></div>
            <span className="p1-select-icon">
              {isLoadingGroups ? "⏳" : "▼"}
            </span>
          </div>
        </div>

        <InputField
          label="لینک اینستاگرام"
          name="instagram"
          value={form.instagram}
          onChange={handleChange}
          placeholder="https://instagram.com/..."
          error={errors.instagram}
        />

        {/* ردیف دوم شبکه */}
        <InputField
          label="کد کالا"
          name="sku"
          value={form.sku}
          onChange={handleChange}
          placeholder="مثلاً 12345"
        />

        <InputField
          label="تخفیف"
          name="discount"
          value={form.discount}
          onChange={handleChange}
          placeholder="مثال: 20"
          error={errors.discount}
          maxLength={2}
        />

        {/* ردیف سوم شبکه */}
        <InputField
          label="ارزش افزوده"
          name="vatPercent"
          value={form.vatPercent}
          onChange={handleChange}
          placeholder="مثال: 10"
          error={errors.vatPercent}
          maxLength={2}
        />

        <InputField
          label="هزینه بسته‌بندی (تومان)"
          name="packagingCost"
          value={form.packagingCost}
          onChange={handleChange}
          placeholder="به هزینه ارسال کل افزوده می شود"
          maxLength={13}
        />
      </div>

      <div className="p1-actions">
        {!isFirst && (
          <button
            type="button"
            className={`p1-btn p1-btn--ghost ${isSubmitting ? 'p1-btn-loading' : ''}`}
            onClick={onPrev}
            disabled={isSubmitting}
          >
            <span className="p1-btn-icon">→</span>
            مرحله قبل
          </button>
        )}

        <button
          type="submit"
          className={`p1-btn p1-btn--primary ${isSubmitting ? 'p1-btn-loading' : ''}`}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            "در حال ارسال..."
          ) : (
            <>
              {isLast ? "پایان" : "مرحله بعد"}
              <span className="p1-btn-icon">←</span>
            </>
          )}
        </button>
      </div>
    </form>
  );
}
